/**
 * Hook che espone lo stato di sincronizzazione del client mobile:
 *
 * - `online`: il device ha connessione (basato su `navigator.onLine` + eventi)
 * - `pendingCount`: numero di upload pendenti in coda IndexedDB
 *
 * La coda IndexedDB sarà gestita completamente dal task 12.W.H. Per ora
 * il hook tenta di leggere la tabella `pending_media` se esiste, altrimenti
 * ritorna 0 senza errori. Questo permette di sviluppare l'indicatore prima
 * della coda completa.
 */
import { useEffect, useState } from 'react'

const DB_NAME = 'edr-mobile'
const STORE_NAME = 'pending_media'
const DB_VERSION = 1
const POLL_MS = 5_000

async function leggiCoda(): Promise<number> {
  if (typeof indexedDB === 'undefined') return 0
  return new Promise<number>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const open = indexedDB.open(DB_NAME, DB_VERSION)
    open.onupgradeneeded = () => {
      // crea lo store se non esiste; il task 12.W.H ne aggiungerà gli indici
      const db = open.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    open.onsuccess = () => {
      const db = open.result
      try {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const store = tx.objectStore(STORE_NAME)
        const req = store.count()
        req.onsuccess = () => {
          if (timer) clearTimeout(timer)
          resolve(req.result || 0)
          db.close()
        }
        req.onerror = () => {
          if (timer) clearTimeout(timer)
          resolve(0)
          db.close()
        }
      } catch {
        resolve(0)
        db.close()
      }
    }
    open.onerror = () => resolve(0)
    open.onblocked = () => resolve(0)
    // safety timeout in caso di blocchi imprevisti
    timer = setTimeout(() => resolve(0), 2_000)
  })
}

export interface SyncStatus {
  online: boolean
  pendingCount: number
}

export function useSyncStatus(): SyncStatus {
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )
  const [pendingCount, setPendingCount] = useState(0)

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    let alive = true
    const refresh = async () => {
      const n = await leggiCoda()
      if (alive) setPendingCount(n)
    }
    void refresh()
    const id = setInterval(() => { void refresh() }, POLL_MS)

    return () => {
      alive = false
      clearInterval(id)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { online, pendingCount }
}
